import React, { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import { FaShieldAlt, FaSave, FaInfoCircle, FaFolder, FaChevronDown, FaChevronRight } from "react-icons/fa";
import { toast } from "react-toastify";

export default function RolePermissionsModal({ role, onClose }) {
  const [modulesList, setModulesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const { token } = useAuth();

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${server}/permissions/role/${role.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          const rawData = res.data.data || [];
          setModulesList(rawData);
          // Expand all by default
          const exp = {};
          rawData.forEach((m) => {
            exp[m.id] = true;
          });
          setExpandedModules(exp);
        }
      } catch (error) {
        toast.error("Failed to load permissions matrix for role");
      } finally {
        setLoading(false);
      }
    };
    if (role?.id && token) fetchPermissions();
  }, [role, token]);

  const toggleExpand = (modId) => {
    setExpandedModules((prev) => ({ ...prev, [modId]: !prev[modId] }));
  };

  // Toggle Page Access for a top-level Module
  const handleToggleModule = (modId) => {
    setModulesList((prev) =>
      prev.map((mod) => {
        if (mod.id !== modId) return mod;

        const nextVal = !mod.canView;
        const updatedSubModules = (mod.subModules || []).map((sub) => ({
          ...sub,
          canView: nextVal,
          canCreate: nextVal,
          canEdit: nextVal,
          canDelete: nextVal,
        }));

        return {
          ...mod,
          canView: nextVal,
          canCreate: nextVal,
          canEdit: nextVal,
          canDelete: nextVal,
          subModules: updatedSubModules,
        };
      })
    );
  };

  // Toggle Page Access for a SubModule
  const handleToggleSubModule = (modId, subId) => {
    setModulesList((prev) =>
      prev.map((mod) => {
        if (mod.id !== modId) return mod;

        const updatedSubModules = (mod.subModules || []).map((sub) => {
          if (sub.id !== subId) return sub;
          const nextVal = !sub.canView;
          return {
            ...sub,
            canView: nextVal,
            canCreate: nextVal,
            canEdit: nextVal,
            canDelete: nextVal,
          };
        });

        // Enable top-level module access if any submodule has access granted
        const anySubHasAccess = updatedSubModules.some((s) => s.canView);

        return {
          ...mod,
          canView: mod.canView || anySubHasAccess,
          canCreate: mod.canView || anySubHasAccess,
          canEdit: mod.canView || anySubHasAccess,
          canDelete: mod.canView || anySubHasAccess,
          subModules: updatedSubModules,
        };
      })
    );
  };

  // Global Quick Action Toggles
  const handleToggleAll = (value) => {
    setModulesList((prev) =>
      prev.map((mod) => {
        const updatedSubModules = (mod.subModules || []).map((sub) => ({
          ...sub,
          canView: value,
          canCreate: value,
          canEdit: value,
          canDelete: value,
        }));

        return {
          ...mod,
          canView: value,
          canCreate: value,
          canEdit: value,
          canDelete: value,
          subModules: updatedSubModules,
        };
      })
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = [];
      modulesList.forEach((mod) => {
        payload.push({
          moduleId: mod.id,
          subModuleId: null,
          code: mod.code,
          canView: mod.canView,
          canCreate: mod.canView,
          canEdit: mod.canView,
          canDelete: mod.canView,
        });

        (mod.subModules || []).forEach((sub) => {
          payload.push({
            moduleId: mod.id,
            subModuleId: sub.id,
            code: sub.code,
            canView: sub.canView,
            canCreate: sub.canView,
            canEdit: sub.canView,
            canDelete: sub.canView,
          });
        });
      });

      const res = await axios.post(`${server}/permissions/role/${role.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        toast.success(`Page access permissions saved successfully for '${role.name}'`);
        onClose();
      }
    } catch (error) {
      toast.error("Failed to save role permissions");
    } finally {
      setSaving(false);
    }
  };

  const isAdminRole =
    role?.id === 1 ||
    ["admin", "superadmin", "administrator"].includes(
      (role?.name || "").toLowerCase()
    );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-4xl rounded-2xl shadow-2xl p-6 flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-150">
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-100 dark:border-slate-800 mb-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl">
              <FaShieldAlt size={18} />
            </div>
            <div>
              <h3 className="text-base font-black text-gray-900 dark:text-white leading-tight">
                Role Access Permissions: {role?.name}
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold flex items-center gap-1.5 mt-0.5">
                <FaInfoCircle className="text-indigo-500 shrink-0" size={11} />
                <span>Manage single-checkbox page access for each system module.</span>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-lg font-bold"
          >
            ×
          </button>
        </div>

        {/* Admin Access Info Banner */}
        {isAdminRole && (
          <div className="bg-sky-50 dark:bg-sky-950/50 border border-sky-200 dark:border-sky-800 p-3 rounded-xl mb-4 text-xs font-bold text-sky-800 dark:text-sky-300 flex items-center gap-2 shrink-0">
            <FaShieldAlt className="text-sky-600 dark:text-sky-400 shrink-0" size={14} />
            <span>
              <strong>Administrator Access:</strong> Admin roles automatically bypass restrictions and are granted full access permissions across all modules and system capabilities.
            </span>
          </div>
        )}

        {/* Quick Action Bar */}
        <div className="bg-gray-50 dark:bg-slate-800/80 p-3 rounded-xl mb-4 flex flex-wrap items-center justify-between gap-3 text-xs font-bold shrink-0">
          <span className="text-gray-600 dark:text-slate-300">Quick Access Controls:</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleToggleAll(true)}
              className="px-3.5 py-1.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition shadow-xs flex items-center gap-1.5"
            >
              Grant Full Access to All
            </button>
            <button
              onClick={() => handleToggleAll(false)}
              className="px-3.5 py-1.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition shadow-xs flex items-center gap-1.5"
            >
              Revoke All Page Access
            </button>
          </div>
        </div>

        {/* Matrix Table */}
        <div className="overflow-y-auto flex-1 custom-scrollbar border border-gray-200 dark:border-slate-800 rounded-xl">
          {loading ? (
            <div className="py-20 text-center text-xs font-bold text-indigo-600">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
              Loading module permissions matrix...
            </div>
          ) : modulesList.length === 0 ? (
            <div className="py-16 text-center text-xs font-semibold text-gray-400 italic">
              No modules defined yet.
            </div>
          ) : (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-slate-800/80 text-gray-600 dark:text-slate-300 font-black uppercase text-[10px] sticky top-0 z-10 border-b border-gray-200 dark:border-slate-700">
                  <th className="p-3.5">Module / SubModule</th>
                  <th className="p-3.5">System Code</th>
                  <th className="p-3.5 text-center bg-indigo-50/70 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 w-48">
                    PAGE ACCESS (FULL ACCESS)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800 font-bold text-gray-800 dark:text-slate-200">
                {modulesList.map((mod) => {
                  const isExpanded = Boolean(expandedModules[mod.id]);
                  const hasSubModules = mod.subModules && mod.subModules.length > 0;

                  return (
                    <React.Fragment key={`mod-${mod.id}`}>
                      {/* Top-Level Module Row */}
                      <tr className="bg-slate-50 dark:bg-slate-800/60 font-black hover:bg-indigo-50/40 dark:hover:bg-slate-800 transition">
                        <td className="p-3.5 font-black text-gray-900 dark:text-white">
                          <div className="flex items-center gap-2">
                            {hasSubModules ? (
                              <button
                                type="button"
                                onClick={() => toggleExpand(mod.id)}
                                className="text-gray-400 hover:text-indigo-600 transition"
                              >
                                {isExpanded ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                              </button>
                            ) : (
                              <span className="w-3" />
                            )}
                            <FaFolder className="text-indigo-600 dark:text-indigo-400" size={14} />
                            <span>{mod.title}</span>
                            <span className="text-[9px] bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-bold px-1.5 py-0.5 rounded border border-indigo-200 dark:border-indigo-800">
                              MODULE
                            </span>
                          </div>
                        </td>
                        <td className="p-3.5 font-mono text-[10px] text-indigo-600 dark:text-indigo-400">
                          {mod.code}
                        </td>

                        {/* Single Page Access Checkbox */}
                        <td className="p-3.5 text-center bg-indigo-50/20 dark:bg-indigo-950/10">
                          <div className="flex flex-col items-center">
                            <input
                              type="checkbox"
                              checked={mod.canView}
                              onChange={() => handleToggleModule(mod.id)}
                              className="w-5 h-5 accent-indigo-600 cursor-pointer"
                            />
                            <span className={`text-[10px] font-black mt-1 ${mod.canView ? "text-indigo-600 dark:text-indigo-400" : "text-rose-500 dark:text-rose-400"}`}>
                              {mod.canView ? "Full Access Granted" : "Access Denied"}
                            </span>
                          </div>
                        </td>
                      </tr>

                      {/* Nested SubModule Rows */}
                      {isExpanded &&
                        (mod.subModules || []).map((sub) => (
                          <tr key={`sub-${sub.id}`} className="hover:bg-gray-50 dark:hover:bg-slate-800/40 transition">
                            <td className="p-3.5 pl-9 font-bold text-gray-800 dark:text-slate-200">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-300 dark:text-slate-600 font-mono">└─</span>
                                <span>{sub.title}</span>
                              </div>
                            </td>
                            <td className="p-3.5 font-mono text-[10px] text-gray-500">
                              {sub.code}
                            </td>

                            {/* SubModule Single Page Access Checkbox */}
                            <td className="p-3.5 text-center">
                              <div className="flex flex-col items-center">
                                <input
                                  type="checkbox"
                                  checked={sub.canView}
                                  onChange={() => handleToggleSubModule(mod.id, sub.id)}
                                  className="w-5 h-5 accent-indigo-600 cursor-pointer"
                                />
                                <span className={`text-[10px] font-black mt-1 ${sub.canView ? "text-indigo-600 dark:text-indigo-400" : "text-rose-500 dark:text-rose-400"}`}>
                                  {sub.canView ? "Full Access Granted" : "Access Denied"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-slate-800 mt-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-200 transition font-bold text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl font-black transition shadow-xs flex items-center gap-2 text-xs"
          >
            <FaSave size={12} /> {saving ? "Saving..." : "Save Role Permissions"}
          </button>
        </div>
      </div>
    </div>
  );
}

